<template lang="pug">
span(v-if="error") {{ $t("error") }}
span(v-else-if="loading && users.length === 0", v-loading="true") 
span.users.user-stack(v-else-if="hasData && props.type === 'stack'") 
  .flex.justify-between.items-center(v-for="user in users", :key="user.id")
    .flex.items-center
      template(v-if="showAvatar")
        Avatar(:user="user", :size="avatarSize")
        .margin(:style="{ width: `${(avatarSize ?? 30) / 4}px` }")
      span.capitalize(v-if="showName") {{ `${user.firstName} ${user.lastName}` }}
    el-icon.cursor-pointer.user-remove(
      v-if="closable",
      @click="() => onClose(user)"
    ) 
      Delete
span.users.smx-comma-separated-row.flex(
  v-else-if="hasData && props.type === 'list'"
) 
  .flex.justify-between.items-center.flex-row(
    v-for="user in users",
    :key="user.id"
  )
    .flex.flex-row.items-center.flex-nowrap.whitespace-nowrap
      template(v-if="showAvatar")
        Avatar(:user="user", :size="avatarSize")
        .margin(:style="{ width: `${(avatarSize ?? 30) / 4}px` }")
      .capitalize(v-if="showName") {{ `${user.firstName} ${user.lastName}` }}
    el-icon.cursor-pointer.user-remove(
      v-if="closable",
      @click="() => onClose(user)"
    ) 
      Delete
p(v-else) -
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import {
  type PaginatedLoadState,
  stateError,
  stateInitial,
  stateLoading,
  stateLoaded,
} from "../../../models/state";
import { type KeyCloakUser } from "../../../services/userManagementKeyCloak";
import { useUserInformationStore } from "../../../stores/userInformation";
import Avatar from "@shared/components/elements/Avatar.vue";

const props = withDefaults(
  defineProps<{
    userIds?: string[];
    showAvatar?: boolean;
    avatarSize?: number | string | null;
    showName?: boolean;
    type?: "list" | "stack";
    closable?: boolean;
  }>(),
  {
    type: "list",
    showName: true,
    showAvatar: false,
    avatarSize: null,
    closable: false,
    userIds: () => [],
  },
);

const emit = defineEmits(["close"]);

const filteredUserIds = computed(
  () => props.userIds?.filter((id) => id !== null && id !== undefined) || [],
);

const hasData = computed(() => {
  return filteredUserIds.value && filteredUserIds.value.length > 0;
});

const userInformationStore = useUserInformationStore();

const loadStates = computed<PaginatedLoadState<KeyCloakUser>[]>(() =>
  (filteredUserIds.value ?? []).map(
    (id) =>
      userInformationStore.userInformationCache.get(id) ?? { ...stateInitial },
  ),
);

const users = computed(() =>
  loadStates.value
    .map((item) => item.result)
    .filter((item) => item !== null && item !== undefined),
);

const loading = computed(() =>
  loadStates.value.reduce((agg, curr) => agg || curr.loading, false),
);
const loaded = computed(() =>
  loadStates.value.reduce((agg, curr) => agg && curr.loaded, true),
);
const error = computed(() =>
  loadStates.value.reduce((agg, curr) => agg || curr.error, false),
);

async function loadData() {
  if (!hasData.value) {
    return;
  }
  await userInformationStore.loadUserInformation(filteredUserIds.value);
}

onMounted(() => {
  loadData();
});

watch(() => filteredUserIds.value, loadData);

function onClose(user: KeyCloakUser) {
  emit("close", user.id);
}
</script>

<style scoped>
.user-stack {
  display: flex;
  flex-direction: column;
}

.user-stack > * + * {
  margin-top: 1rem;
}
</style>
